import { useEffect, useRef, useState } from 'react';

import { skipToken, useQuery } from '@apollo/client/react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Typography,
} from '@mui/material';
import { loginByMagicToken } from '@realadvisor/auth-client';
import { Image } from '@realadvisor/image';
import { useSearchParams } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import { useLocale } from '@shared/hooks/locale';

export const VALIDATE_INVITATION = gql(/* GraphQL */ `
  query ValidateInvitation($token: String!) {
    validate_invitation(token: $token) {
      success
      tenant_id
      magic_token
    }
  }
`);

export const InvitationAccept = () => {
  const { t } = useLocale();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [isValidated, setIsValidated] = useState(false);
  const [error, setError] = useState('');
  const loginStarted = useRef(false);

  const {
    data,
    loading,
    error: invitationError,
  } = useQuery(
    VALIDATE_INVITATION,
    token ? { variables: { token } } : skipToken,
  );

  useEffect(() => {
    if (isValidated || loginStarted.current) {
      return;
    }

    if (invitationError) {
      setError(invitationError.message);
      return;
    }

    const invitation = data?.validate_invitation;
    if (!invitation) {
      return;
    }

    if (
      !invitation.success ||
      !invitation.magic_token ||
      !invitation.tenant_id
    ) {
      setError(t('Please check the link or try again.'));
      return;
    }

    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('token', invitation.magic_token);
    currentUrl.searchParams.set('tenant_id', invitation.tenant_id);
    window.history.replaceState({}, '', currentUrl.toString());

    loginStarted.current = true;
    loginByMagicToken()
      .then(() => setIsValidated(true))
      .catch(() => {
        loginStarted.current = false;
        setError(t('Please check the link or try again.'));
      });
  }, [data, invitationError, isValidated, t]);

  if (!token) {
    return (
      <Container
        maxWidth="sm"
        sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}
      >
        <Paper sx={{ p: 4, textAlign: 'center', width: '100%' }}>
          <Box
            sx={{
              margin: '0 auto',
              mb: 3,
              width: 256,
            }}
          >
            <Image
              src="/realadvisor-logo.svg"
              alt="RealAdvisor Logo"
              options={{ w: 256 }}
            />
          </Box>
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('No invitation token provided.')}
          </Alert>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('Please use the complete invitation link or contact support.')}
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="sm"
      sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}
    >
      <Paper sx={{ p: 4, textAlign: 'center', width: '100%' }}>
        <Box
          sx={{
            margin: '0 auto',
            mb: 4,
            width: 256,
          }}
        >
          <Image
            src="/realadvisor-logo.svg"
            alt="RealAdvisor Logo"
            options={{ w: 256 }}
          />
        </Box>

        {loading && (
          <Box>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {t('Just a moment...')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t('Processing your invitation')}
            </Typography>
          </Box>
        )}

        {error && (
          <Box>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t('Please check the link or try again.')}
            </Typography>
          </Box>
        )}

        {isValidated && (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              {t('Your account has been successfully activated.')}
            </Alert>
            <Typography variant="h6" gutterBottom>
              {t('Welcome aboard!')}
            </Typography>
            <Button
              variant="contained"
              onClick={() => {
                window.location.assign('/');
              }}
              sx={{
                background: 'linear-gradient(135deg, #54beda 0%, #0a39b7 100%)',
                px: 4,
                py: 1.5,
                margin: '0 auto',
              }}
            >
              {t('Continue')}
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default InvitationAccept;
